import { StringKey } from '../lang/index';
import { Company } from './company.types';
import { Valuation } from './events.types';
import { StakeholderType } from './stakeholder.types';

export interface CommonStats {
  converted: number;
  convertedPercentage: number;
  diluted: number;
  dilutedPercentage: number;
  issued: number;
  issuedPercentage: number;
  votingPercentage: number;
  votingRight: number;
}

export interface StakeholderStats extends CommonStats {
  avgSharePrice: number;
  dividends: number;
  id: string;
  invested: number;
  name: string;
  shareClasses: ShareClassStats[];
  type: StakeholderType;
}

export interface StakeholderTypeStats extends CommonStats {
  avgSharePrice: number;
  dividends: number;
  invested: number;
  shareClasses: ShareClassStats[];
  type: StakeholderType;
}

export interface ShareClassStats extends CommonStats {
  avgSharePrice: number;
  dividends: number;
  id: string;
  investment: number;
  name: string;
  noStakeholders: number;
}

export interface SharePlanStats {
  dilluted: number;
  id: string;
  name: string;
}

export interface PoolStats {
  converted: number;
  convertedPercentage: number;
  dilluted: number;
  dillutedPercentage: number;
  dilutedUnvested: number;
  dilutedUnvestedPercentage: number;
  id: string;
  issued: number;
  issuedPercentage: number;
  isTerminated: boolean;
  name: string;
  shareClassId: string;
  terminationDate: Date;
  unvested: number;
  unvestedPercentage: number;
}

export interface Stats {
  pools: PoolStats[];
  shareClasses: ShareClassStats[];
  sharePlans: SharePlanStats[];
  stakeholders: StakeholderStats[];
  stakeholdersTypes: StakeholderTypeStats[];
}

export enum DetailsSortVariant {
  BY_SHARE_CLASS = 'by-share-class',
  BY_STAKEHOLDER = 'by-stakeholder',
}

export const detailsSortVariantTitle: Record<DetailsSortVariant, StringKey> = {
  [DetailsSortVariant.BY_STAKEHOLDER]: 'BY_STAKEHOLDER',
  [DetailsSortVariant.BY_SHARE_CLASS]: 'BY_SHARE_CLASS',
};

export const customizeSortVariantTitle: Record<DetailsSortVariant, StringKey> = {
  [DetailsSortVariant.BY_STAKEHOLDER]: 'STAKEHOLDER',
  [DetailsSortVariant.BY_SHARE_CLASS]: 'SHARE_CLASS',
};

export const detailsSortVariantTitleMap = Object.entries(detailsSortVariantTitle);
export const customizeSortVariantTitleMap = Object.entries(customizeSortVariantTitle);

export enum Column {
  AVERAGE_SHARE_PRICE = 'average-share-price',
  COMMON_STOCK = 'common-stock',
  CONVERTED_PERCENTAGE = 'converted-percentage',
  CONVERTED_SHARES = 'converted-shares',
  DILUTED = 'diluted',
  DILUTED_SHARES = 'diluted-shares',
  DIVIDENDS = 'dividends',
  EMPTY = 'empty',
  INVESTMENT = 'investment',
  ISSUED = 'issued',
  NAME = 'name',
  NO_ISSUED = 'no-issued',
  NO_OF_STAKEHOLDERS = 'no-of-stakeholders',
  OTHER_SHARE_CLASSES = 'other-share-classes',
  SHARE_CLASS_NAME = 'share-class-name',
  STAKEHOLDER_NAME = 'stakeholder-name',
  TYPE = 'type',
  VOTING = 'voting',
  VOTING_RIGHTS = 'voting-rights',
}

export const columnTitle: Record<Column, StringKey> = {
  [Column.ISSUED]: 'ISSUED_PERCENTAGE',
  [Column.DILUTED]: 'DILUTED_PERCENTAGE',
  [Column.VOTING]: 'VOTING_PERCENTAGE',
  [Column.NO_ISSUED]: 'NO_ISSUED',
  [Column.DILUTED_SHARES]: 'DILUTED_SHARES',
  [Column.COMMON_STOCK]: 'COMMON_STOCK',
  [Column.OTHER_SHARE_CLASSES]: 'OTHER_SHARE_CLASSES',
  [Column.INVESTMENT]: 'INVESTMENT',
  [Column.DIVIDENDS]: 'DIVIDENDS',
  [Column.STAKEHOLDER_NAME]: 'STAKEHOLDER_NAME',
  [Column.SHARE_CLASS_NAME]: 'SHARE_CLASS_NAME',
  [Column.AVERAGE_SHARE_PRICE]: 'AVERAGE_SHARE_PRICE',
  [Column.VOTING_RIGHTS]: 'VOTING_RIGHTS',
  [Column.NO_OF_STAKEHOLDERS]: 'NO_OF_STAKEHOLDERS',
  [Column.TYPE]: 'TYPE',
  [Column.NAME]: 'NAME',
  [Column.CONVERTED_SHARES]: 'CONVERTED_BASIS',
  [Column.CONVERTED_PERCENTAGE]: 'CONVERTED_BASIS_PERCENT',
  [Column.EMPTY]: 'EMPTY',
};

export type StakeholderColumns =
  | Column.AVERAGE_SHARE_PRICE
  | Column.COMMON_STOCK
  | Column.CONVERTED_PERCENTAGE
  | Column.CONVERTED_SHARES
  | Column.DILUTED
  | Column.DILUTED_SHARES
  | Column.DIVIDENDS
  | Column.INVESTMENT
  | Column.ISSUED
  | Column.NAME
  | Column.NO_ISSUED
  | Column.OTHER_SHARE_CLASSES
  | Column.STAKEHOLDER_NAME
  | Column.TYPE
  | Column.VOTING
  | Column.VOTING_RIGHTS;

export type ShareClassColumns =
  | Column.AVERAGE_SHARE_PRICE
  | Column.CONVERTED_PERCENTAGE
  | Column.CONVERTED_SHARES
  | Column.DILUTED
  | Column.DILUTED_SHARES
  | Column.DIVIDENDS
  | Column.INVESTMENT
  | Column.ISSUED
  | Column.NAME
  | Column.NO_ISSUED
  | Column.NO_OF_STAKEHOLDERS
  | Column.SHARE_CLASS_NAME
  | Column.STAKEHOLDER_NAME
  | Column.TYPE
  | Column.VOTING
  | Column.VOTING_RIGHTS;

export enum ViewBy {
  STAKEHOLDERS = 'stakeholders',
  TYPE = 'type',
}

export enum CapTableOrderingMode {
  CUSTOM = 'custom',
  DEFAULT = 'default',
}

export const capTableOrderingModeTitle: Record<CapTableOrderingMode, StringKey> = {
  [CapTableOrderingMode.DEFAULT]: 'DEFAULT',
  [CapTableOrderingMode.CUSTOM]: 'CAP_TABLE_ORDERING_CUSTOM',
};

export const capTableOrderingModeDescription: Record<CapTableOrderingMode, StringKey> = {
  [CapTableOrderingMode.DEFAULT]: 'CAP_TABLE_ORDERING_DEFAULT_DESCRIPTION',
  [CapTableOrderingMode.CUSTOM]: 'CAP_TABLE_ORDERING_CUSTOM_DESCRIPTION',
};

/** Secondary sort applied to the stakeholders inside one type group of a custom ordering. */
export enum WithinTypeSort {
  ALPHABETICAL_ASC = 'alphabetical-asc',
  ALPHABETICAL_DESC = 'alphabetical-desc',
  OWNERSHIP_ASC = 'ownership-asc',
  OWNERSHIP_DESC = 'ownership-desc',
}

export const withinTypeSortTitle: Record<WithinTypeSort, StringKey> = {
  [WithinTypeSort.OWNERSHIP_DESC]: 'OWNERSHIP_PERCENT_HIGH_TO_LOW',
  [WithinTypeSort.OWNERSHIP_ASC]: 'OWNERSHIP_PERCENT_LOW_TO_HIGH',
  [WithinTypeSort.ALPHABETICAL_ASC]: 'ALPHABETICAL_A_TO_Z',
  [WithinTypeSort.ALPHABETICAL_DESC]: 'ALPHABETICAL_Z_TO_A',
};

/**
 * Every stakeholder type, in the sequence the Ordering tab offers before the viewer drags
 * anything: founders, then the investor kinds, then the people a company brings on. The list is
 * exhaustive on purpose — the tab lists every type whether or not the company has one of it.
 */
export const defaultStakeholderTypeOrder: StakeholderType[] = [
  StakeholderType.FOUNDER,
  StakeholderType.ANGEL,
  StakeholderType.VCS,
  StakeholderType.PES,
  StakeholderType.FAMILY_OFFICE,
  StakeholderType.INVESTMENT_BANKS,
  StakeholderType.SPV,
  StakeholderType.OTHER_INST,
  StakeholderType.EMPLOYEE,
  StakeholderType.ADVISOR,
  StakeholderType.BOARD_MEMBERS,
];

/**
 * One configuration shared by both view modes (`DetailsSortVariant`) — the Ordering tab is not set
 * per mode. `typeOrder` and `withinType` are kept while `mode` is `DEFAULT` so that toggling back
 * to `CUSTOM` restores what the viewer had arranged.
 */
export type CapTableOrdering = {
  mode: CapTableOrderingMode;
  typeOrder: StakeholderType[];
  withinType: WithinTypeSort;
};

export const defaultCapTableOrdering = (): CapTableOrdering => ({
  mode: CapTableOrderingMode.DEFAULT,
  typeOrder: [...defaultStakeholderTypeOrder],
  withinType: WithinTypeSort.OWNERSHIP_DESC,
});

export type ColumnVariant = ShareClassColumns | StakeholderColumns;

export const detailsColumns: Record<DetailsSortVariant, ColumnVariant[]> = {
  [DetailsSortVariant.BY_STAKEHOLDER]: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.DIVIDENDS,
    Column.AVERAGE_SHARE_PRICE,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
  ],
  [DetailsSortVariant.BY_SHARE_CLASS]: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.INVESTMENT,
    Column.DIVIDENDS,
    Column.AVERAGE_SHARE_PRICE,
    Column.NO_OF_STAKEHOLDERS,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
  ],
};

export const detailsViewByColumns: Record<
  'all' | ViewBy,
  Record<DetailsSortVariant, ColumnVariant[]>
> = {
  all: {
    [DetailsSortVariant.BY_STAKEHOLDER]: [
      Column.NAME,
      Column.ISSUED,
      Column.DILUTED,
      Column.VOTING,
      Column.NO_ISSUED,
      Column.DILUTED_SHARES,
      Column.VOTING_RIGHTS,
      Column.COMMON_STOCK,
      Column.OTHER_SHARE_CLASSES,
      Column.INVESTMENT,
      Column.DIVIDENDS,
      Column.AVERAGE_SHARE_PRICE,
      Column.CONVERTED_SHARES,
      Column.CONVERTED_PERCENTAGE,
    ],
    [DetailsSortVariant.BY_SHARE_CLASS]: [
      Column.NAME,
      Column.ISSUED,
      Column.DILUTED,
      Column.VOTING,
      Column.NO_ISSUED,
      Column.DILUTED_SHARES,
      Column.VOTING_RIGHTS,
      Column.INVESTMENT,
      Column.DIVIDENDS,
      Column.AVERAGE_SHARE_PRICE,
      Column.NO_OF_STAKEHOLDERS,
      Column.CONVERTED_SHARES,
      Column.CONVERTED_PERCENTAGE,
    ],
  },
  [ViewBy.STAKEHOLDERS]: {
    [DetailsSortVariant.BY_STAKEHOLDER]: [
      Column.STAKEHOLDER_NAME,
      Column.TYPE,
      Column.ISSUED,
      Column.DILUTED,
      Column.VOTING,
      Column.NO_ISSUED,
      Column.DILUTED_SHARES,
      Column.VOTING_RIGHTS,
      Column.COMMON_STOCK,
      Column.OTHER_SHARE_CLASSES,
      Column.INVESTMENT,
      Column.DIVIDENDS,
      Column.AVERAGE_SHARE_PRICE,
      Column.CONVERTED_SHARES,
      Column.CONVERTED_PERCENTAGE,
    ],
    [DetailsSortVariant.BY_SHARE_CLASS]: [
      Column.STAKEHOLDER_NAME,
      Column.TYPE,
      Column.ISSUED,
      Column.DILUTED,
      Column.VOTING,
      Column.NO_ISSUED,
      Column.DILUTED_SHARES,
      Column.VOTING_RIGHTS,
      Column.INVESTMENT,
      Column.DIVIDENDS,
      Column.AVERAGE_SHARE_PRICE,
      Column.NO_OF_STAKEHOLDERS,
      Column.CONVERTED_SHARES,
      Column.CONVERTED_PERCENTAGE,
    ],
  },
  [ViewBy.TYPE]: {
    [DetailsSortVariant.BY_STAKEHOLDER]: [
      Column.TYPE,
      Column.ISSUED,
      Column.DILUTED,
      Column.VOTING,
      Column.NO_ISSUED,
      Column.DILUTED_SHARES,
      Column.VOTING_RIGHTS,
      Column.COMMON_STOCK,
      Column.OTHER_SHARE_CLASSES,
      Column.INVESTMENT,
      Column.DIVIDENDS,
      Column.AVERAGE_SHARE_PRICE,
      Column.CONVERTED_SHARES,
      Column.CONVERTED_PERCENTAGE,
    ],
    [DetailsSortVariant.BY_SHARE_CLASS]: [
      Column.SHARE_CLASS_NAME,
      Column.ISSUED,
      Column.DILUTED,
      Column.VOTING,
      Column.NO_ISSUED,
      Column.DILUTED_SHARES,
      Column.VOTING_RIGHTS,
      Column.INVESTMENT,
      Column.DIVIDENDS,
      Column.AVERAGE_SHARE_PRICE,
      Column.NO_OF_STAKEHOLDERS,
      Column.CONVERTED_SHARES,
      Column.CONVERTED_PERCENTAGE,
    ],
  },
};

export type CustomViewExportSnapshot = {
  /** Mirrors the Ordering tab so the spreadsheet groups and sorts rows like the page does. */
  ordering?: CapTableOrdering;

  viewByColumns?: Record<DetailsSortVariant, Record<ViewBy, boolean>>;

  viewColumns?: Record<DetailsSortVariant, Column[]>;
};

export type Snapshot = {
  company: Company;
  companyIncorporation?: { id: string } | null;
  createdAt: Date;
  createdByCron: boolean;
  dilutedShares: number;
  id: string;
  investorsCount: number;
  issuedShares: number;
  lifetimeInvestmentEquity: number;
  poolDilutedShares: number;
  poolGrantedShares: number;
  postMoneyValuation: number;
  stats: Stats;
  valuation?: null | Valuation;
};

// `diluted` is optional only for list bodies from a backend that predates it (added 2026-10-22);
// the valuation chart falls back to `issued` for those.
export type SnapshotSummaryStakeholder = Partial<Pick<StakeholderStats, 'diluted'>> &
  Pick<StakeholderStats, 'id' | 'issued' | 'issuedPercentage' | 'name'>;

// The list endpoints (`/snapshots`, `/cap-table-snapshots`) return this slim shape — full
// `stats` (multi-MB per page) is only served by the by-id routes; see use-snapshot.ts.
export type SnapshotListItem = {
  hasCompleteStats?: boolean;
  stats: { stakeholders: SnapshotSummaryStakeholder[] };
} & Omit<Snapshot, 'company' | 'stats'>;
